import { shallow } from 'zustand/shallow';
import { type MediaType } from '../types';
import { MEDIA_TYPE } from './constants';

export const getAvatarTextInitials = (fullName: string) =>
  fullName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

export const miliSecondsToMMSS = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const paddedMinutes = minutes.toString().padStart(2, '0');
  const paddedSeconds = seconds.toString().padStart(2, '0');

  if (hours > 0) {
    const paddedHours = hours.toString().padStart(2, '0');
    return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
  }

  return `${paddedMinutes}:${paddedSeconds}`;
};

export function getHueFromName(name: string): number {
  let hash = 0;

  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Convert hash to 0–360 range
  const hue = Math.abs(hash) % 360;
  return hue;
}

function hsvToRgb(h: number, s: number, v: number) {
  s /= 100;
  v /= 100;

  let c = v * s;
  let x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  let m = v - c;

  let r = 0,
    g = 0,
    b = 0;

  if (h >= 0 && h < 60) {
    [r, g, b] = [c, x, 0];
  } else if (h >= 60 && h < 120) {
    [r, g, b] = [x, c, 0];
  } else if (h >= 120 && h < 180) {
    [r, g, b] = [0, c, x];
  } else if (h >= 180 && h < 240) {
    [r, g, b] = [0, x, c];
  } else if (h >= 240 && h < 300) {
    [r, g, b] = [x, 0, c];
  } else if (h >= 300 && h < 360) {
    [r, g, b] = [c, 0, x];
  }

  return `rgb(${Math.round((r + m) * 255)}, ${Math.round((g + m) * 255)}, ${Math.round((b + m) * 255)})`;
}

export function getColorFromName(name: string): string {
  const hue = getHueFromName(name);
  return hsvToRgb(hue, 45, 35);
}

export function safeParseJson<T>(jsonString: string): T | null {
  try {
    return JSON.parse(jsonString) as T;
  } catch (error) {
    console.error('Failed to parse JSON:', error);
    return null;
  }
}

export const getMediaType = (track: any): MediaType => {
  if (track.getType() === 'audio') {
    return MEDIA_TYPE.AUDIO;
  }
  if (track.getVideoType() === 'desktop') {
    return MEDIA_TYPE.SCREENSHARE;
  }
  return MEDIA_TYPE.VIDEO;
};

export function differenceBy<T, U>(
  array: T[],
  values: U[],
  iteratee: (value: T | U) => any,
): T[] {
  const valuesToExclude = new Set(values.map(iteratee));
  return array.filter(item => !valuesToExclude.has(iteratee(item)));
}

export function sleep(ms: number = 1000): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function isArrayOfObjEqual(arr1: any[], arr2: any[]): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }

  for (let i = 0; i < arr1.length; i++) {
    if (!shallow(arr1[i], arr2[i])) {
      return false;
    }
  }

  return true;
}

export function isDeviceEqual(
  device1: MediaDeviceInfo | undefined,
  device2: MediaDeviceInfo | undefined,
) {
  return (
    device1?.deviceId === device2?.deviceId && device1?.label === device2?.label
  );
}

export function getDefaultDevice<T extends MediaDeviceInfo>(devices: T[]) {
  return devices.find(device => device.deviceId === 'default') || devices[0];
}
/**
 * Returns a promise that resolves when the given Zustand store
 * satisfies the provided predicate. Resolves immediately if the
 * condition is already met. Includes a timeout to avoid hanging
 * forever (defaults to 5 000 ms).
 */
export function waitForStoreState<T>(
  store: {
    getState: () => T;
    subscribe: (listener: (state: T) => void) => () => void;
  },
  predicate: (state: T) => boolean,
  timeoutMs = 5000,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (predicate(store.getState())) {
      resolve();
      return;
    }

    const timer = setTimeout(() => {
      unsubscribe();
      reject(new Error('waitForStoreState timed out'));
    }, timeoutMs);

    const unsubscribe = store.subscribe(state => {
      if (predicate(state)) {
        clearTimeout(timer);
        unsubscribe();
        resolve();
      }
    });
  });
}
